import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { trackEvent } from "@/lib/analytics";

export function Footer() {
  const { settings } = useSettings();
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-wood-deep text-cream grain" data-testid="site-footer">
      <div className="container-ed py-20 md:py-24">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-5">
            <img
              src="/logo-osteria-aterrana.png"
              alt="Osteria Aterrana"
              className="h-16 w-auto brightness-0 invert opacity-90"
            />
            <p className="mt-3 eyebrow text-gold">{settings.tagline || "Vino e cucina tra le pietre"}</p>
            <p className="mt-5 text-sm leading-relaxed text-cream/65 max-w-sm">
              Piccola osteria nel borgo storico di Aterrana, a Montoro. Cucina tradizionale irpina e
              campana, prodotti scelti con attenzione.
            </p>
            <Link
              to="/prenota"
              data-testid="footer-prenota"
              className="mt-8 inline-flex px-7 py-4 bg-gold text-wood-deep text-[0.7rem] uppercase tracking-[0.22em] hover:bg-cream transition-colors duration-500"
            >
              Prenota un tavolo
            </Link>
          </div>

          <div className="md:col-span-4 space-y-4 text-sm text-cream/70">
            <p className="eyebrow text-gold">Dove siamo</p>
            <p className="flex gap-3">
              <MapPin size={16} strokeWidth={1.4} className="mt-0.5 shrink-0 text-cream/40" />
              <span>
                {settings.address || "Via Madonna di Montevergine 34"}
                <br />
                {settings.address_line2 || "Borgo storico di Aterrana · Montoro (AV)"}
              </span>
            </p>
            <p className="flex gap-3">
              <Phone size={16} strokeWidth={1.4} className="mt-0.5 shrink-0 text-cream/40" />
              <a href={`tel:${settings.phone_link || "+393311091971"}`} className="link-underline">
                {settings.phone || "331 10 91 971"}
              </a>
            </p>
            <p className="flex gap-3">
              <Mail size={16} strokeWidth={1.4} className="mt-0.5 shrink-0 text-cream/40" />
              <a
                href={`mailto:${settings.email || "osteriaaterrana@outlook.it"}`}
                data-testid="footer-email"
                className="link-underline break-all"
              >
                {settings.email || "osteriaaterrana@outlook.it"}
              </a>
            </p>
            <p className="whitespace-pre-line text-cream/55">
              {settings.hours_note || "Mercoledì – Sabato, cena · Domenica, pranzo\nLunedì e martedì chiuso"}
            </p>
            <a
              href={settings.instagram || "https://www.instagram.com/osteria_aterrana/"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram"
          onClick={() => trackEvent("click_instagram", { position: "footer" })}
              className="inline-flex items-center gap-2 link-underline"
            >
              <Instagram size={16} strokeWidth={1.4} /> Instagram
            </a>
            {settings.facebook && (
              <a
                href={settings.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="ml-5 inline-flex items-center gap-2 link-underline"
              >
                <Facebook size={16} strokeWidth={1.4} /> Facebook
              </a>
            )}
          </div>

          <nav className="md:col-span-3 space-y-3 text-sm" aria-label="Navigazione footer">
            <p className="eyebrow text-gold mb-4">Pagine</p>
            {[
              ["/la-cucina", "La cucina"],
              ["/menu", "Menu"],
              ["/il-borgo", "Il borgo"],
              ["/progetti-futuri", "Progetti futuri"],
              ["/diario-dal-borgo", "Diario dal Borgo"],
              ["/galleria", "Galleria"],
              ["/contatti", "Contatti"],
              ["/privacy", "Privacy"],
              ["/cookie-policy", "Cookie Policy"],
            ].map(([to, label]) => (
              <Link key={to} to={to} className="block text-cream/70 hover:text-cream link-underline w-fit">
                {label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="mt-16 pt-6 border-t border-cream/10 flex flex-col sm:flex-row justify-between gap-3 text-[0.7rem] tracking-wider text-cream/40">
          <span>© {year} Osteria Aterrana · P.IVA 03083930648</span>
          <Link to="/admin" className="hover:text-cream/70">Area riservata</Link>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
